/**
 * Minimal fragmented-MP4 (fMP4) analyser.
 *
 * What it does:
 *  - Splits the file on real box boundaries (init segment + moof/mdat groups),
 *    so every encrypted segment is independently appendable to MSE.
 *  - Reads codec strings, size and timescales from the moov box, so the player
 *    asks the browser for the correct MIME type instead of guessing.
 *  - Reads each group's start time (tfdt) so the player can seek.
 *
 * The input must be a *fragmented* MP4. Create one with:
 *   ffmpeg -i in.mp4 -c copy -movflags frag_keyframe+empty_moov+default_base_moof out.mp4
 */

const fourcc = (v, o) =>
  String.fromCharCode(v.getUint8(o), v.getUint8(o + 1), v.getUint8(o + 2), v.getUint8(o + 3));
const hex2 = (n) => (n & 0xff).toString(16).padStart(2, "0");

function readBoxes(view, start, end) {
  const boxes = [];
  let o = start;
  while (o + 8 <= end) {
    let size = view.getUint32(o);
    const type = fourcc(view, o + 4);
    let header = 8;
    if (size === 1) {
      if (o + 16 > end) break;
      size = Number(view.getBigUint64(o + 8));
      header = 16;
    } else if (size === 0) {
      size = end - o;
    }
    if (size < header || o + size > end) break;
    boxes.push({ type, start: o, size, header, body: o + header, end: o + size });
    o += size;
  }
  return boxes;
}

const kids = (view, box, type) =>
  readBoxes(view, box.body, box.end).filter((b) => !type || b.type === type);
const kid = (view, box, type) => kids(view, box, type)[0];

function findTag(buf, entry, tag) {
  const a = tag.charCodeAt(0), b = tag.charCodeAt(1), c = tag.charCodeAt(2), d = tag.charCodeAt(3);
  for (let i = entry.body; i + 4 <= entry.end; i++) {
    if (buf[i] === a && buf[i + 1] === b && buf[i + 2] === c && buf[i + 3] === d) return i;
  }
  return -1;
}

function parseEsds(buf, o, end) {
  let oti = 0x40;
  let aot = 2;
  while (o < end) {
    const tag = buf[o++];
    let len = 0;
    for (let i = 0; i < 4; i++) {
      const b = buf[o++];
      len = (len << 7) | (b & 0x7f);
      if (!(b & 0x80)) break;
    }
    if (tag === 0x03) {
      const flags = buf[o + 2];
      o += 3;
      if (flags & 0x80) o += 2;
      if (flags & 0x40) o += 1 + buf[o];
      if (flags & 0x20) o += 2;
    } else if (tag === 0x04) {
      oti = buf[o];
      o += 13;
    } else if (tag === 0x05) {
      aot = buf[o] >> 3;
      if (aot === 31) aot = 32 + (((buf[o] & 7) << 3) | (buf[o + 1] >> 5));
      break;
    } else {
      o += len;
    }
  }
  return oti === 0x40 ? `mp4a.40.${aot}` : `mp4a.${hex2(oti).toUpperCase()}`;
}

function codecFromEntry(buf, entry) {
  const t = entry.type;
  if (t === "avc1" || t === "avc3") {
    const p = findTag(buf, entry, "avcC");
    if (p < 0) return null;
    const b = p + 4;
    return `${t}.${hex2(buf[b + 1])}${hex2(buf[b + 2])}${hex2(buf[b + 3])}`;
  }
  if (t === "hvc1" || t === "hev1") {
    const p = findTag(buf, entry, "hvcC");
    if (p < 0) return null;
    const b = p + 4;
    const x = buf[b + 1];
    const space = ["", "A", "B", "C"][(x >> 6) & 3];
    const tier = (x >> 5) & 1 ? "H" : "L";
    const idc = x & 31;
    const compat = ((buf[b + 2] << 24) | (buf[b + 3] << 16) | (buf[b + 4] << 8) | buf[b + 5]) >>> 0;
    let rev = 0;
    for (let i = 0; i < 32; i++) rev = ((rev << 1) | ((compat >>> i) & 1)) >>> 0;
    const c = Array.from(buf.slice(b + 6, b + 12));
    while (c.length > 1 && c[c.length - 1] === 0) c.pop();
    return `${t}.${space}${idc}.${rev.toString(16).toUpperCase()}.${tier}${buf[b + 12]}.${c
      .map((n) => hex2(n).toUpperCase())
      .join(".")}`;
  }
  if (t === "av01") {
    const p = findTag(buf, entry, "av1C");
    if (p < 0) return null;
    const b = p + 4;
    const profile = buf[b + 1] >> 5;
    const level = buf[b + 1] & 31;
    const tier = (buf[b + 2] >> 7) & 1 ? "H" : "M";
    const high = (buf[b + 2] >> 6) & 1;
    const twelve = (buf[b + 2] >> 5) & 1;
    const depth = high ? (twelve ? 12 : 10) : 8;
    return `av01.${profile}.${String(level).padStart(2, "0")}${tier}.${String(depth).padStart(2, "0")}`;
  }
  if (t === "mp4a") {
    const p = findTag(buf, entry, "esds");
    if (p < 0) return "mp4a.40.2";
    return parseEsds(buf, p + 8, entry.end);
  }
  if (t === "Opus") return "opus";
  if (t === "fLaC") return "flac";
  if (t === "ac-3" || t === "ec-3") return t;
  return null;
}

export function parseMoov(buf) {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const moov = readBoxes(view, 0, buf.byteLength).find((b) => b.type === "moov");
  if (!moov) throw new Error("moov box could not be parsed.");

  const info = { timescale: 0, duration: 0, fragDuration: 0, tracks: new Map() };

  const mvhd = kid(view, moov, "mvhd");
  if (mvhd) {
    const v = view.getUint8(mvhd.body);
    info.timescale = view.getUint32(mvhd.body + (v === 1 ? 20 : 12));
    info.duration =
      v === 1 ? Number(view.getBigUint64(mvhd.body + 24)) : view.getUint32(mvhd.body + 16);
  }
  const mvex = kid(view, moov, "mvex");
  const mehd = mvex && kid(view, mvex, "mehd");
  if (mehd) {
    const v = view.getUint8(mehd.body);
    info.fragDuration =
      v === 1 ? Number(view.getBigUint64(mehd.body + 4)) : view.getUint32(mehd.body + 4);
  }

  for (const trak of kids(view, moov, "trak")) {
    const tkhd = kid(view, trak, "tkhd");
    const mdia = kid(view, trak, "mdia");
    if (!tkhd || !mdia) continue;
    const mdhd = kid(view, mdia, "mdhd");
    if (!mdhd) continue;

    const tv = view.getUint8(tkhd.body);
    const id = view.getUint32(tkhd.body + (tv === 1 ? 20 : 12));
    const width = view.getUint32(tkhd.end - 8) >>> 16;
    const height = view.getUint32(tkhd.end - 4) >>> 16;
    const mv = view.getUint8(mdhd.body);
    const timescale = view.getUint32(mdhd.body + (mv === 1 ? 20 : 12));
    const hdlr = kid(view, mdia, "hdlr");
    const handler = hdlr ? fourcc(view, hdlr.body + 8) : "";

    let codec = null;
    const minf = kid(view, mdia, "minf");
    const stbl = minf && kid(view, minf, "stbl");
    const stsd = stbl && kid(view, stbl, "stsd");
    if (stsd) {
      const entry = readBoxes(view, stsd.body + 8, stsd.end)[0];
      if (entry) codec = codecFromEntry(buf, entry);
    }
    info.tracks.set(id, { id, width, height, timescale, handler, codec });
  }
  return info;
}

function moofStartTime(buf, tracks) {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const moof = readBoxes(view, 0, buf.byteLength).find((b) => b.type === "moof");
  if (!moof) return null;
  for (const traf of kids(view, moof, "traf")) {
    const tfhd = kid(view, traf, "tfhd");
    const tfdt = kid(view, traf, "tfdt");
    if (!tfhd || !tfdt) continue;
    const track = tracks.get(view.getUint32(tfhd.body + 4));
    if (!track || !track.timescale) continue;
    const v = view.getUint8(tfdt.body);
    const t =
      v === 1 ? Number(view.getBigUint64(tfdt.body + 4)) : view.getUint32(tfdt.body + 4);
    return t / track.timescale;
  }
  return null;
}

async function scanTopLevel(blob) {
  const boxes = [];
  const total = blob.size;
  let o = 0;
  while (o + 8 <= total) {
    const head = new DataView(await blob.slice(o, Math.min(o + 16, total)).arrayBuffer());
    let size = head.getUint32(0);
    const type = fourcc(head, 4);
    if (size === 1) {
      if (head.byteLength < 16) break;
      size = Number(head.getBigUint64(8));
    } else if (size === 0) {
      size = total - o;
    }
    if (size < 8 || o + size > total) {
      throw new Error(`The MP4 looks corrupted: box "${type}" at byte ${o} runs past the end of the file.`);
    }
    boxes.push({ type, start: o, end: o + size, size });
    o += size;
  }
  return boxes;
}

/**
 * @returns {Promise<{
 *   parts: {start:number,end:number,init?:boolean,startTime:number|null}[],
 *   mime: string, codecs: string[], width: number, height: number,
 *   duration: number, durationEstimated: boolean, timeIndexed: boolean
 * }>}
 */
export async function planFragments(blob, { segmentBytes = 4 * 1024 * 1024 } = {}) {
  const boxes = await scanTopLevel(blob);
  const moovBox = boxes.find((b) => b.type === "moov");
  const firstMoof = boxes.findIndex((b) => b.type === "moof");

  if (!moovBox) throw new Error("This is not a valid MP4 (no moov box).");
  if (firstMoof < 0) {
    throw new Error(
      "This MP4 is not fragmented, so it cannot be streamed with MSE. Convert it first:\n" +
        "ffmpeg -i input.mp4 -c copy -movflags frag_keyframe+empty_moov+default_base_moof output.mp4"
    );
  }
  if (moovBox.start > boxes[firstMoof].start) throw new Error("Unsupported MP4: moov must come before the first moof.");

  const moovBytes = new Uint8Array(await blob.slice(moovBox.start, moovBox.end).arrayBuffer());
  const info = parseMoov(moovBytes);

  const parts = [{ start: 0, end: boxes[firstMoof].start, init: true, startTime: null }];
  const groups = [];
  let cur = null;
  let prevType = null;
  for (let i = firstMoof; i < boxes.length; i++) {
    const b = boxes[i];
    const boundary = prevType === "mdat" && (b.type === "moof" || b.type === "styp" || b.type === "sidx");
    if (cur && boundary && cur.end - cur.start >= segmentBytes) {
      groups.push(cur);
      cur = null;
    }
    if (!cur) cur = { start: b.start, end: b.end, moof: null };
    else cur.end = b.end;
    if (b.type === "moof" && !cur.moof) cur.moof = b;
    prevType = b.type;
  }
  if (cur) groups.push(cur);

  let timeIndexed = true;
  let last = -Infinity;
  for (const g of groups) {
    let startTime = null;
    if (g.moof) {
      const mb = new Uint8Array(await blob.slice(g.moof.start, g.moof.end).arrayBuffer());
      startTime = moofStartTime(mb, info.tracks);
    }
    if (startTime === null || startTime < last) timeIndexed = false;
    if (startTime !== null) last = startTime;
    parts.push({ start: g.start, end: g.end, startTime });
  }

  const tracks = [...info.tracks.values()];
  const video = tracks.find((t) => t.handler === "vide");
  const codecs = tracks.map((t) => t.codec).filter(Boolean);
  const codecsComplete = tracks.length > 0 && codecs.length === tracks.length;
  const mime = `${video ? "video" : "audio"}/mp4${codecsComplete ? `; codecs="${codecs.join(",")}"` : ""}`;

  let duration = 0;
  let durationEstimated = false;
  if (info.fragDuration && info.timescale) duration = info.fragDuration / info.timescale;
  else if (info.duration && info.timescale) duration = info.duration / info.timescale;
  const media = parts.filter((p) => !p.init);
  if (!duration && timeIndexed && media.length >= 2) {
    const a = media[media.length - 1].startTime;
    const b = media[media.length - 2].startTime;
    duration = a + (a - b);
    durationEstimated = true;
  }

  return {
    parts,
    mime,
    codecs,
    width: video?.width || 0,
    height: video?.height || 0,
    duration,
    durationEstimated,
    timeIndexed,
  };
}